import React, { cloneElement } from 'react';

import { useStripe } from '@stripe/react-stripe-js';
import type {
	StripeCardCvcElementChangeEvent,
	StripeCardExpiryElementChangeEvent,
	StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js';

import { ErrorMessage } from '@change/ds-forms';

import type { WithTokenParams } from 'src/app/shared/types';
import type { PaymentMethodSaveOptions, PaymentType } from 'src/app/shared/utils/payments';

import type { Props as SubmitButtonProps } from '../../../components/SubmitButton';
import { StripeCreditCardElements } from '../../components/StripeCreditCardElements';

import { useStripeForm } from './hooks/useStripeForm';

export type Props = {
	beforeToken: (paymentType: PaymentType) => Promise<boolean>;
	email: string;
	name: string;
	paymentMethodSaveOptions: PaymentMethodSaveOptions;
	countryCode: string;
	currency: string;
	submitButton: React.ReactElement<SubmitButtonProps>;
	phoneNumberInput?: React.JSX.Element | null;
	onCardNumberFieldFocus: () => void;
	onCardExpiryFieldFocus: () => void;
	onCardCVCFieldFocus: () => void;
	onChange?: (
		e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
	) => void;
	onTokenError: (paymentType: PaymentType, err: Error) => void;
	onTokenInvalid: (paymentType: PaymentType, err: Error) => void;
	validate: (paymentType: PaymentType) => Promise<boolean>;
	withToken: (params: WithTokenParams) => Promise<void>;
	prePaymentChecks?: (paymentType: PaymentType) => Promise<boolean>;
};

export function StripeForm({
	beforeToken,
	email,
	name,
	paymentMethodSaveOptions,
	countryCode,
	currency,
	submitButton,
	phoneNumberInput,
	onCardNumberFieldFocus,
	onCardExpiryFieldFocus,
	onCardCVCFieldFocus,
	onChange: propOnChange = () => {},
	onTokenError,
	onTokenInvalid,
	validate,
	withToken,
	prePaymentChecks,
}: Props): React.JSX.Element {
	const stripe = useStripe();

	const {
		data: { errorMessage, validationErrors },
		actions: { handleSubmit, validateStripeElementOnChange },
	} = useStripeForm({
		beforeToken,
		email,
		name,
		paymentMethodSaveOptions,
		countryCode,
		currency,
		onTokenError,
		onTokenInvalid,
		validate,
		withToken,
		prePaymentChecks,
	});

	function creditCardFieldOnChange(
		e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
	) {
		propOnChange(e);
		validateStripeElementOnChange(e);
	}

	return (
		<>
			<div className="mb-4">
				<form data-testid="stripe-form" onSubmit={handleSubmit}>
					<div className="mb-6">
						<StripeCreditCardElements
							validationErrors={validationErrors}
							onCardNumberFieldFocus={onCardNumberFieldFocus}
							onCardExpiryFieldFocus={onCardExpiryFieldFocus}
							onCardCVCFieldFocus={onCardCVCFieldFocus}
							onChange={creditCardFieldOnChange}
						/>
					</div>
					{phoneNumberInput}
					<div className="flex justify-center">
						{cloneElement(submitButton, {
							disabled: submitButton.props.disabled || !stripe,
							// note that this will only work if submitButton supports a loading property
							loading: submitButton.props.loading,
						})}
					</div>
				</form>
			</div>
			{errorMessage && (
				<div data-qa="stripe-form-error-message">
					<ErrorMessage className="mb-4" size="large">
						{errorMessage}
					</ErrorMessage>
				</div>
			)}
		</>
	);
}
